<script setup lang="ts">
import { useThemeStore } from '@/stores/theme';
import { useTracking } from '@/composables/useTracking';
import ConsentBanner from '@/components/ConsentBanner.vue';

useThemeStore();
// First-party pageview tracking — POSTs to same-origin /api/track.php which
// writes into the shared openbmcs MySQL DB. Both the client and the server
// are GDPR consent-gated, so the ConsentBanner below must be accepted before
// any rows land. The route guard inside the composable handles SPA navigation.
useTracking();
</script>

<template>
  <div class="bg-stage" aria-hidden="true">
    <!-- Page-wide gradient halo backdrop. The PNG is 1440×6163 (≈6.85vh-tall),
         positioned-fixed so it parallaxes naturally as you scroll the very tall
         single-page layout. Non-interactive. -->
    <div class="bg-gradient" />
  </div>

  <RouterView />

  <!-- First-visit cookie/consent banner — without an "Accept" click here the
       server-side track.php / events.php endpoints silently drop pings (no
       cookie = no row). Same component openbmcs uses. -->
  <ConsentBanner />
</template>

<style>
:root {
  /* Body font override — OpenCCMS uses Mukta for body, Palanquin for display. */
  --ccms-sans: 'Mukta', 'Inter', system-ui, -apple-system, sans-serif;
  --ccms-display: 'Palanquin', 'Mukta', system-ui, sans-serif;
}

html, body, #app { min-height: 100%; }

body {
  margin: 0;
  background: var(--bmcs-bg);
  color: var(--bmcs-text);
  font-family: var(--ccms-sans);
}

/* Light-only — live OpenCCMS has no dark mode. */
body { background: #ffffff; color: #050505; position: relative; }

/* Fixed-positioned full-viewport gradient backdrop. The PNG is 1440×6163;
   `cover` stretches it to fill the viewport (the visible portion changes
   subtly as the page scrolls because the element stays viewport-locked,
   which approximates the live site's parallax-on-scroll effect). */
.bg-stage {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none; overflow: hidden;
}
.bg-gradient {
  position: absolute;
  inset: 0;
  background-image: url('/images/site-bg-gradient.png');
  background-size: cover;
  background-repeat: no-repeat;
  background-position: top center;
}

/* Routed <main> sits above the fixed bg-stage so the gradient is behind
   content. Nav (sticky with z-index:50 in SiteNav.vue) and footer are not
   targeted here so the nav can layer ON TOP of main (which is what makes
   the hero image appear to pass *under* the menu). */
#app > main { position: relative; z-index: 1; }
</style>
